import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import { getAnonId } from "../lib/analytics";

/**
 * Wraps the rest of the app in a password gate. Renders the login form
 * when the backend reports `authenticated: false` (i.e. PASSWORD is set
 * server-side and the user hasn't signed in yet); otherwise renders
 * children. While the whoami query is in flight the gate shows a tiny
 * placeholder so the page doesn't flicker.
 */
export function AuthGate({ children }: { children: React.ReactNode }) {
  const qc = useQueryClient();
  const whoami = useQuery({
    queryKey: ["whoami"],
    queryFn: api.whoami,
    // Re-check periodically so the cookie expiring or the env var
    // changing eventually kicks the user back to the login form
    // without needing a hard refresh.
    refetchInterval: 5 * 60_000,
    retry: false,
    staleTime: 60_000,
  });

  if (whoami.isLoading) {
    return (
      <div className="flex items-center justify-center h-screen text-[12px] text-muted">
        Loading…
      </div>
    );
  }

  // A failed whoami must not sit on "Loading…" for ever. With
  // `retry: false` any 500 or dropped connection left the gate with no
  // data and no error surface, so the whole app rendered as a blank
  // screen with no way back short of a manual reload.
  if (whoami.isError || !whoami.data) {
    return (
      <div className="flex flex-col items-center justify-center h-screen gap-3 px-6 text-center">
        <div className="text-[13px] text-text font-semibold">
          Can&rsquo;t reach the portal API
        </div>
        <div className="text-[11.5px] text-muted max-w-sm">
          {(whoami.error as Error | null)?.message ??
            "The server didn't respond. It may be restarting."}
        </div>
        <button
          type="button"
          onClick={() => void whoami.refetch()}
          disabled={whoami.isFetching}
          className="text-[11px] uppercase tracking-wider px-4 py-2 rounded border border-wb text-wb hover:bg-wb hover:text-white disabled:opacity-50"
        >
          {whoami.isFetching ? "Retrying…" : "Retry"}
        </button>
      </div>
    );
  }

  if (whoami.data.authenticated) return <>{children}</>;

  return (
    <LoginForm
      onSuccess={() => qc.invalidateQueries({ queryKey: ["whoami"] })}
    />
  );
}

/** The slurry-only view lives at /slurry (and anywhere a slurry login lands). */
export function isSlurryPath(): boolean {
  return typeof window !== "undefined" && window.location.pathname === "/slurry";
}

function LoginForm({ onSuccess }: { onSuccess: () => void }) {
  const [password, setPassword] = useState("");
  const mutation = useMutation({
    // Send the browser's anonymous id alongside the password so the
    // server-side login event can be attributed to a specific browser
    // for the admin distinct-user count.
    mutationFn: () => api.login(password, getAnonId()),
    onSuccess,
  });

  const errorMessage = mutation.error
    ? (mutation.error as Error).message
    : null;

  return (
    <div className="flex items-center justify-center h-screen bg-bg">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          mutation.mutate();
        }}
        className="card w-[320px] p-6 flex flex-col gap-4"
      >
        <div>
          <div className="text-[10.5px] uppercase tracking-[0.22em] text-dim">
            Ward &amp; Burke
          </div>
          <h1 className="text-[16px] font-semibold text-text mt-1">
            {isSlurryPath() ? "Slurry monitoring" : "Tunnel portal"}
          </h1>
        </div>

        <label className="flex flex-col gap-1.5">
          <span className="text-[11px] uppercase tracking-wider text-dim">
            Password
          </span>
          <input
            type="password"
            value={password}
            autoFocus
            autoComplete="current-password"
            onChange={(e) => setPassword(e.target.value)}
            className="bg-bg border border-border1 rounded px-3 py-2 text-[13px] text-text focus:outline-none focus:border-wb"
          />
        </label>

        {errorMessage && (
          <div className="text-[11.5px] text-wb">{errorMessage}</div>
        )}

        <button
          type="submit"
          disabled={mutation.isPending || password.length === 0}
          className="text-[11px] uppercase tracking-wider px-4 py-2 rounded border border-wb text-wb hover:bg-wb hover:text-white disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {mutation.isPending ? "Signing in…" : "Sign in"}
        </button>
      </form>
    </div>
  );
}
